import { useEffect, useRef, useState } from "react";
import {
  Check,
  CirclePlay,
  CircleX,
  Heart,
  ListMusic,
  MicVocal,
  Pause,
  Play,
  Radio,
  RadioTower,
  Search,
  SkipBack,
  SkipForward,
  Square,
  Volume,
  Volume2
} from "lucide-react";
import type { MusicArtistMatch, MusicState } from "@home-control/shared";
import { fetchCommandResult, sendCommand } from "../../api";
import { useHome } from "../state";
import { SectionHeader } from "../components/SectionHeader";
import { Spinner } from "../components/Spinner";
import "./music.css";

const searchDebounceMs = 700;
const searchPollMs = 400;
const searchPollAttempts = 80;

/** Music detail page (spec §5): now playing, stations, artist search, playlists. */
export function MusicPage() {
  const { state, token, canControlDevices, isPending, command } = useHome();
  const music = state?.music;

  if (!music) {
    return (
      <main className="page music-page">
        <SectionHeader title="Music" />
        <p className="placeholder-note">No speaker data yet. Music controls appear once the house reports in.</p>
      </main>
    );
  }

  const disabled = !music.available || !canControlDevices;

  return (
    <main className="page music-page">
      <SectionHeader title="Music" />
      {!music.available && (
        <p className="music-unavailable-note">
          Speakers are unavailable right now. Controls will wake up when the system reconnects.
        </p>
      )}
      <NowPlayingPanel music={music} disabled={disabled} isPending={isPending} onCommand={command} />
      <StationsPanel music={music} disabled={disabled} isPending={isPending} onCommand={command} />
      <ArtistSearchPanel
        token={token}
        disabled={disabled}
        isPending={isPending}
        onCommand={command}
      />
      {(music.playlists?.length ?? 0) > 0 && (
        <PlaylistsPanel music={music} disabled={disabled} isPending={isPending} onCommand={command} />
      )}
    </main>
  );
}

type CommandFn = ReturnType<typeof useHome>["command"];

/* Now playing (spec §5.1) --------------------------------------------------- */

function NowPlayingPanel({
  music,
  disabled,
  isPending,
  onCommand
}: {
  music: MusicState;
  disabled: boolean;
  isPending: (label: string) => boolean;
  onCommand: CommandFn;
}) {
  const active = music.playing || music.paused;
  const transportPending = isPending("music-transport");
  const volumePending = isPending("music-volume");

  // Remember the station last seeding the queue so Play can restart it after Stop.
  const lastStationRef = useRef<string | undefined>(music.stationId);
  useEffect(() => {
    if (music.stationId) lastStationRef.current = music.stationId;
  }, [music.stationId]);

  const restartStationId = music.stationId ?? lastStationRef.current ?? music.stations[0]?.id;
  const currentStationLabel = music.stations.find((station) => station.id === music.stationId)?.label;

  const title = !music.available
    ? "Speakers unavailable"
    : active
      ? music.title ?? "Playing"
      : "Nothing playing";
  const subline = !music.available
    ? "Controls come back when the speakers reconnect."
    : active
      ? music.artist ?? currentStationLabel ?? "Whole house"
      : "Pick a station below";

  function handleTransport() {
    if (music.playing) {
      void onCommand("music-transport", { kind: "pauseMusic" });
    } else if (music.paused) {
      void onCommand("music-transport", { kind: "resumeMusic" });
    } else if (restartStationId) {
      void onCommand("music-transport", { kind: "playRadio", station: restartStationId });
    }
  }

  // Draft-while-dragging volume: the slider tracks a local value so state
  // refreshes cannot yank the thumb; the command is sent only on release.
  const [draftVolume, setDraftVolume] = useState<number | null>(null);
  const volume = draftVolume ?? clampVolume(music.volume);

  function commitVolume() {
    if (draftVolume === null) return;
    if (draftVolume !== clampVolume(music.volume)) {
      void onCommand("music-volume", { kind: "setMusicVolume", volume: draftVolume });
    }
    setDraftVolume(null);
  }

  const playDisabled = disabled || transportPending || (!music.playing && !music.paused && !restartStationId);
  const stopDisabled = disabled || transportPending || !active;

  return (
    <section className={panelClass(disabled)} aria-disabled={disabled}>
      <div className="music-now-row">
        <div className="music-now-text">
          <h2 className="music-now-title">{title}</h2>
          <p className="music-now-subline">{subline}</p>
        </div>
        {active && music.title ? (
          <div className="music-now-actions">
            <button
              type="button"
              className={`music-like-button${music.liked ? " liked" : ""}`}
              disabled={disabled || music.liked === true}
              onClick={() => void onCommand("music-like", { kind: "likeCurrentTrack" })}
              title={music.liked ? "In your Liked Songs" : "Add to Liked Songs"}
              aria-label={music.liked ? "In your Liked Songs" : "Add to Liked Songs"}
            >
              <Heart size={15} strokeWidth={2.5} fill={music.liked ? "currentColor" : "none"} />
            </button>
            <button
              type="button"
              className="music-channel-button"
              disabled={disabled || transportPending}
              onClick={() => void onCommand("music-transport", { kind: "playRadioFromCurrent" })}
              title="Make a channel based on this"
            >
              <RadioTower size={14} strokeWidth={2.5} />
              Channel
            </button>
          </div>
        ) : null}
      </div>
      <div className="music-transport-row">
        <button
          type="button"
          className="music-transport-button music-skip-button"
          aria-label="Previous track"
          disabled={disabled || transportPending || !active}
          onClick={() => void onCommand("music-transport", { kind: "previousTrack" })}
        >
          <SkipBack size={15} strokeWidth={2.5} fill="currentColor" />
        </button>
        <button
          type="button"
          className="music-transport-button music-play-button"
          disabled={playDisabled}
          onClick={handleTransport}
        >
          {transportPending ? (
            <Spinner size={15} />
          ) : music.playing ? (
            <Pause size={15} strokeWidth={2.5} fill="currentColor" />
          ) : (
            <Play size={15} strokeWidth={2.5} fill="currentColor" />
          )}
          {music.playing ? "Pause" : "Play"}
        </button>
        <button
          type="button"
          className="music-transport-button music-skip-button"
          aria-label="Next track"
          disabled={disabled || transportPending || !active}
          onClick={() => void onCommand("music-transport", { kind: "nextTrack" })}
        >
          <SkipForward size={15} strokeWidth={2.5} fill="currentColor" />
        </button>
        <button
          type="button"
          className={`music-transport-button music-stop-button${active ? " active" : ""}`}
          disabled={stopDisabled}
          onClick={() => void onCommand("music-transport", { kind: "stopMusic" })}
        >
          <Square size={14} strokeWidth={2.5} fill="currentColor" />
          Stop
        </button>
      </div>
      {(music.players?.length ?? 0) > 1 ? (
        <div className="music-speakers-block">
          <span className="music-volume-label">Speakers</span>
          <div className="music-speakers-row">
            {music.players?.map((player) => (
              <button
                key={player}
                type="button"
                className={`music-speaker-chip${music.player === player ? " active" : ""}`}
                disabled={disabled || music.player === player}
                onClick={() => void onCommand("music-player", { kind: "setMusicPlayer", player })}
              >
                {player}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className={`music-volume-block${volumePending ? " pending" : ""}`}>
        <div className="music-volume-header">
          <span className="music-volume-label">Volume</span>
          <span className="music-volume-value">{volume}%</span>
        </div>
        <div className="music-volume-row">
          <Volume size={14} className="music-volume-icon" />
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={volume}
            disabled={disabled || volumePending}
            aria-label="Volume"
            onChange={(event) => setDraftVolume(Number(event.target.value))}
            onPointerUp={commitVolume}
            onKeyUp={commitVolume}
            onBlur={commitVolume}
          />
          <Volume2 size={14} className="music-volume-icon" />
        </div>
      </div>
    </section>
  );
}

function clampVolume(volume: number): number {
  return Math.min(100, Math.max(0, Math.round(volume)));
}

/* Stations (spec §5.2) ------------------------------------------------------ */

function StationsPanel({
  music,
  disabled,
  isPending,
  onCommand
}: {
  music: MusicState;
  disabled: boolean;
  isPending: (label: string) => boolean;
  onCommand: CommandFn;
}) {
  const activePlayback = music.playing || music.paused;
  return (
    <section className={panelClass(disabled)} aria-disabled={disabled}>
      <h3 className="music-subheader">Stations</h3>
      <div className="music-row-list">
        {music.stations.map((station) => {
          const current = station.id === music.stationId;
          const pending = isPending(`music-station-${station.id}`);
          return (
            <button
              key={station.id}
              type="button"
              className={`music-row${current ? " current" : ""}`}
              disabled={disabled || pending}
              onClick={() => void onCommand(`music-station-${station.id}`, { kind: "playRadio", station: station.id })}
            >
              <span className="music-row-icon">
                {current && activePlayback ? (
                  <RadioTower size={17} className="music-tinted" />
                ) : (
                  <Radio size={17} className={current ? "music-tinted" : "music-muted"} />
                )}
              </span>
              <span className={`music-row-label${current ? " current" : ""}`}>{station.label}</span>
              <span className="music-row-accessory">
                {pending ? <Spinner size={15} /> : current ? <Check size={13} strokeWidth={3} className="music-tinted" /> : null}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

/* Artist search (spec §5.3) -------------------------------------------------- */

function ArtistSearchPanel({
  token,
  disabled,
  isPending,
  onCommand
}: {
  token: string;
  disabled: boolean;
  isPending: (label: string) => boolean;
  onCommand: CommandFn;
}) {
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<MusicArtistMatch[] | null>(null);
  const [searchError, setSearchError] = useState("");

  // The trimmed query the user currently has typed; results returning for an
  // older query are dropped so fast typing never shows stale matches.
  const currentQueryRef = useRef("");
  const debounceRef = useRef<number | null>(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    };
  }, []);

  function isStale(searched: string): boolean {
    return !mountedRef.current || currentQueryRef.current !== searched;
  }

  async function runSearch(searched: string) {
    setSearching(true);
    setSearchError("");
    try {
      const id = await sendCommand(token, { kind: "searchMusicArtists", query: searched });
      for (let attempt = 0; attempt < searchPollAttempts; attempt += 1) {
        await delay(searchPollMs);
        if (isStale(searched)) return;
        const result = await fetchCommandResult(token, id).catch(() => null);
        if (isStale(searched)) return;
        if (!result) continue;
        if (!result.ok) {
          setSearchError(result.error ?? "Artist search failed.");
          setSearching(false);
          return;
        }
        const artists = (result.data as { artists?: MusicArtistMatch[] } | undefined)?.artists ?? [];
        setResults(artists);
        setSearching(false);
        return;
      }
      if (!isStale(searched)) {
        setSearchError("Artist search timed out.");
        setSearching(false);
      }
    } catch {
      if (!isStale(searched)) {
        setSearchError("Artist search failed.");
        setSearching(false);
      }
    }
  }

  function scheduleSearch(nextQuery: string, immediate = false) {
    if (debounceRef.current !== null) {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    const trimmed = nextQuery.trim();
    currentQueryRef.current = trimmed;
    if (trimmed.length < 2) {
      setResults(null);
      setSearchError("");
      setSearching(false);
      return;
    }
    if (immediate) {
      void runSearch(trimmed);
    } else {
      debounceRef.current = window.setTimeout(() => void runSearch(trimmed), searchDebounceMs);
    }
  }

  function clearSearch() {
    setQuery("");
    scheduleSearch("");
  }

  return (
    <section className={panelClass(disabled)} aria-disabled={disabled}>
      <h3 className="music-subheader">Play any artist</h3>
      <div className="music-search-field">
        <Search size={15} className="music-muted" />
        <input
          type="text"
          value={query}
          placeholder="Start typing an artist…"
          disabled={disabled}
          aria-label="Search artists"
          onChange={(event) => {
            setQuery(event.target.value);
            scheduleSearch(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") scheduleSearch(query, true);
          }}
        />
        <span className="music-search-accessory">
          {searching ? (
            <Spinner size={16} />
          ) : query.length > 0 ? (
            <button type="button" className="music-search-clear" aria-label="Clear search" onClick={clearSearch}>
              <CircleX size={16} />
            </button>
          ) : null}
        </span>
      </div>
      {searchError ? (
        <p className="music-search-error">{searchError}</p>
      ) : results !== null && results.length === 0 && !searching ? (
        <p className="music-search-empty">No artists found.</p>
      ) : null}
      {(results?.length ?? 0) > 0 && (
        <div className="music-row-list">
          {results?.map((artist) => {
            const pending = isPending(`music-uri-${artist.uri}`);
            return (
              <button
                key={artist.uri}
                type="button"
                className="music-row"
                disabled={disabled || pending}
                onClick={(event) => {
                  event.currentTarget.blur();
                  void onCommand(`music-uri-${artist.uri}`, {
                    kind: "playMusicUri",
                    uri: artist.uri,
                    radioMode: true
                  });
                }}
              >
                <span className="music-row-icon">
                  <MicVocal size={16} className="music-muted" />
                </span>
                <span className="music-row-label">{artist.name}</span>
                <span className="music-row-accessory">
                  {pending ? <Spinner size={15} /> : <CirclePlay size={18} className="music-tinted" />}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

/* Playlists (spec §5.4) ------------------------------------------------------ */

function PlaylistsPanel({
  music,
  disabled,
  isPending,
  onCommand
}: {
  music: MusicState;
  disabled: boolean;
  isPending: (label: string) => boolean;
  onCommand: CommandFn;
}) {
  return (
    <section className={panelClass(disabled)} aria-disabled={disabled}>
      <h3 className="music-subheader">Playlists</h3>
      <div className="music-row-list">
        {music.playlists?.map((playlist) => {
          const pending = isPending(`music-uri-${playlist.uri}`);
          return (
            <button
              key={playlist.uri}
              type="button"
              className="music-row"
              disabled={disabled || pending}
              onClick={() =>
                void onCommand(`music-uri-${playlist.uri}`, {
                  kind: "playMusicUri",
                  uri: playlist.uri,
                  radioMode: false
                })
              }
            >
              <span className="music-row-icon">
                <ListMusic size={16} className="music-muted" />
              </span>
              <span className="music-row-label">{playlist.name}</span>
              <span className="music-row-accessory">
                {pending ? <Spinner size={15} /> : <CirclePlay size={18} className="music-tinted" />}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

/* Shared bits ----------------------------------------------------------------- */

function panelClass(disabled: boolean): string {
  return `music-panel${disabled ? " disabled" : ""}`;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
